'use client';

import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { cn } from '@/lib/utils';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
		variant?: 'default' | 'line';
		fillWidth?: boolean;
	}
>(({ className, variant = 'default', fillWidth, ...props }, ref) => (
	<TabsPrimitive.List
		ref={ref}
		className={cn(
			'inline-flex w-full items-center justify-start',
			variant === 'line' && 'border-b border-grey-200 gap-0 mb-2 relative min-h-0',
			variant === 'default' &&
				'h-10 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground',
			fillWidth && 'grid grid-cols-[repeat(auto-fit,minmax(0,1fr))]',
			!fillWidth && variant === 'line' && 'flex flex-wrap',
			className
		)}
		data-slot="tabs-list"
		data-variant={variant}
		{...props}
	/>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
		variant?: 'default' | 'line';
	}
>(({ className, variant = 'default', ...props }, ref) => (
	<TabsPrimitive.Trigger
		ref={ref}
		className={cn(
			'inline-flex items-center justify-center whitespace-nowrap text-[16px] font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
			variant === 'line' &&
				'rounded-t px-2 py-2 sm:px-4 text-grey-500 data-[state=active]:text-purple-900 data-[state=active]:border-b-2 data-[state=active]:border-purple-900 data-[state=active]:-mb-0.5 hover:bg-purple-50 hover:text-purple-900 focus-visible:bg-purple-50 focus-visible:text-purple-900 focus-visible:border-b-2 focus-visible:border-purple-900 focus-visible:-mb-0.5',
			variant === 'default' &&
				'rounded-md px-3 py-1.5 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
			className
		)}
		data-slot="tabs-trigger"
		data-variant={variant}
		{...props}
	/>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn(
			'mt-0 flex flex-col flex-1 overflow-y-auto p-4 min-h-0 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
			className
		)}
		data-slot="tabs-content"
		{...props}
	/>
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
